import type { AccessPlayer, AccessStatus } from "@69ly/shared";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router";
import { Avatar } from "../components/Avatar.tsx";
import { ErrorState, Loading } from "../components/Status.tsx";
import { errorText } from "../lib/errors.ts";
import { useAccessList, useAddPlayer, useMe, useSetAccess } from "../lib/queries.ts";

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

/** What an admin can do to a player, by their current status. */
const ACTIONS: Record<AccessStatus, { to: AccessStatus; key: string; danger?: boolean }[]> = {
  waiting: [
    { to: "allowed", key: "allow" },
    { to: "refused", key: "refuse", danger: true },
  ],
  allowed: [{ to: "refused", key: "remove", danger: true }],
  refused: [{ to: "allowed", key: "allow" }],
};

function PlayerRow({ player }: { player: AccessPlayer }) {
  const { t } = useTranslation();
  const setAccess = useSetAccess();
  return (
    <li className="access-row">
      <span className="player-cell">
        <Avatar name={player.name} url={player.avatarUrl} />
        <span>
          <span className="access-name">{player.name}</span>
          <span className="muted small mono access-id">{player.steamId}</span>
        </span>
      </span>
      <span className="muted small access-when">{t("access.lastSeen", { when: formatWhen(player.lastSeenAt) })}</span>
      <span className="member-actions">
        {player.isAdmin ? (
          <span className="role-tag role-owner">{t("access.admin")}</span>
        ) : (
          ACTIONS[player.access].map((a) => (
            <button
              key={a.key}
              type="button"
              className={`btn ${a.key === "allow" ? "btn-primary" : "btn-quiet"}${a.danger ? " btn-danger" : ""}`}
              disabled={setAccess.isPending}
              onClick={() => setAccess.mutate({ userId: player.id, access: a.to })}
            >
              {t(`access.${a.key}`)}
            </button>
          ))
        )}
        {setAccess.error && <span className="field-error">{errorText(t, setAccess.error)}</span>}
      </span>
    </li>
  );
}

function Group({ status, players }: { status: AccessStatus; players: AccessPlayer[] }) {
  const { t } = useTranslation();
  return (
    <section className="card" aria-labelledby={`access-${status}`}>
      <h2 id={`access-${status}`} className="eyebrow">
        {t(`access.groups.${status}`)} <span className="count-pill">{players.length}</span>
      </h2>
      {players.length ? (
        <ul className="access-list">
          {players.map((p) => (
            <PlayerRow key={p.id} player={p} />
          ))}
        </ul>
      ) : (
        <p className="muted small">{t(`access.empty.${status}`)}</p>
      )}
    </section>
  );
}

function AddPlayer() {
  const { t } = useTranslation();
  const add = useAddPlayer();
  const [steam, setSteam] = useState("");
  const [name, setName] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    add.mutate(
      { steam, name: name.trim() || undefined },
      {
        onSuccess: () => {
          setSteam("");
          setName("");
        },
      },
    );
  };
  return (
    <section className="card" aria-labelledby="access-add">
      <h2 id="access-add" className="eyebrow">
        {t("access.addTitle")}
      </h2>
      <p className="muted small">{t("access.addHelp")}</p>
      <form className="stack" onSubmit={submit}>
        <label className="field">
          <span>{t("access.steamField")}</span>
          <input
            value={steam}
            onChange={(e) => setSteam(e.target.value)}
            placeholder="https://steamcommunity.com/profiles/7656…"
            required
          />
        </label>
        <label className="field">
          <span>{t("access.nameField")}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={32} />
        </label>
        {add.error && <p className="field-error">{errorText(t, add.error)}</p>}
        {add.isSuccess && <p className="muted small">{t("access.added", { name: add.data.name })}</p>}
        <button type="submit" className="btn btn-secondary" disabled={add.isPending}>
          {t("access.add")}
        </button>
      </form>
    </section>
  );
}

export function AccessPage() {
  const { t } = useTranslation();
  const me = useMe().data;
  const list = useAccessList();
  if (!me?.isAdmin) return <Navigate to="/library" replace />;
  if (list.isPending) return <Loading />;
  if (list.error) return <ErrorState message={errorText(t, list.error)} onRetry={() => void list.refetch()} />;

  const by = (s: AccessStatus) => list.data.players.filter((p) => p.access === s && !(s !== "allowed" && p.isAdmin));
  const allowed = list.data.players.filter((p) => p.access === "allowed" || p.isAdmin);

  return (
    <div className="page access-page">
      <div className="page-head">
        <div>
          <p className="eyebrow">{t("access.eyebrow")}</p>
          <h1 className="page-title">{t("access.title")}</h1>
          <p className="muted">{t(`access.mode.${list.data.mode}`)}</p>
        </div>
      </div>
      <div className="columns">
        <div className="column-main stack">
          <Group status="waiting" players={by("waiting")} />
          <Group status="allowed" players={allowed} />
          <Group status="refused" players={by("refused")} />
        </div>
        <aside className="column-side">
          <AddPlayer />
        </aside>
      </div>
    </div>
  );
}
